import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input } from 'antd';
import InputRequirements from '@components/FormInputs/InputRequirements';
import { useTranslation } from 'react-i18next';
import VisuallyHidden from '@components/UI/utilities/VisuallyHidden';
import { debounce } from 'lodash';

type VerificationCodeValidatorProps = {
  code: string;
  isFocussed: boolean;
  requirementsId: string;
  setMissingSummary: React.Dispatch<React.SetStateAction<string>>;
  setCodeValid: React.Dispatch<React.SetStateAction<boolean>>;
};

const VerificationCodeValidator = ({
  code,
  isFocussed,
  requirementsId,
  setMissingSummary,
  setCodeValid
}: VerificationCodeValidatorProps) => {
  const [tooShort, setTooShort] = useState(true);
  const [tooLong, setTooLong] = useState(true);
  const [containsNonDigits, setContainsNonDigits] = useState(true);

  const checkIfCodeValid = (toCheck: string): void => {
    const isValidCode = checkCodeValidity(toCheck);
    setCodeValid(Boolean(toCheck && isValidCode));
  };

  const debouncedCheckIfCodeValid = useMemo(() => debounce((cd) => checkIfCodeValid(cd), 400), []);

  const checkCodeValidity = useCallback((toCheck = '') => {
    const isTooShort = toCheck.length < 6;
    const isTooLong = toCheck.length > 6;
    const digitsOnly = !!toCheck.length && !/[^\d]/.test(toCheck);

    setTooShort(isTooShort);
    setContainsNonDigits(!digitsOnly);
    setTooLong(isTooLong);

    return !isTooShort && !isTooLong && digitsOnly;
  }, []);

  useEffect(() => {
    debouncedCheckIfCodeValid(code || '');
  }, [checkCodeValidity, debouncedCheckIfCodeValid, code]);

  // After blurring out of the password field, don't wait for a debounce, immediately trigger a password check
  useEffect(() => {
    if (!isFocussed && code) {
      checkIfCodeValid(code);
    }
  }, [checkCodeValidity, checkIfCodeValid, code, isFocussed]);

  const codeRequirements = [
    { ok: !tooShort && !tooLong, description: '6 characters' },
    { ok: !containsNonDigits, description: 'Digits only' }
  ];

  // Generate the missing summary for screen readers
  useEffect(() => {
    const missing = codeRequirements
      .filter((req) => !req.ok)
      .map((req) => req.description)
      .join(', ');

    if (missing && code.length > 0) {
      setMissingSummary(`Verification code requirements not met: ${missing}`);
    } else if (!missing && code.length > 0) {
      setMissingSummary('All verification code requirements met.');
    } else {
      setMissingSummary('');
    }
  }, [code, tooShort, tooLong, containsNonDigits]);

  return <InputRequirements id={requirementsId} title="Should contain" inputRequirements={codeRequirements} />;
};

type VerificationCodeProps = {
  onChange: (code: string) => void;
  setValid?: React.Dispatch<React.SetStateAction<boolean>>;
  id?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: React.AriaAttributes['aria-invalid'];
  'aria-required'?: React.AriaAttributes['aria-required'];
};

const VerificationCode = ({
  onChange = () => {},
  setValid = () => {},
  id = 'verification-code-input',
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired
}: VerificationCodeProps) => {
  const { t } = useTranslation('common');
  const [codeValid, setCodeValid] = useState(false);
  const [code, setCode] = useState('');
  const [illegalCharacterPressed, setIllegalCharacterPressed] = useState(false);
  const [isFocussed, setIsFocussed] = useState(false);
  const [missingSummary, setMissingSummary] = useState('');

  const requirementsId = `${id}-requirements`;
  const successId = `${id}-success`;
  const activeDescribedBy = codeValid ? successId : requirementsId;
  const combinedDescribedBy = [ariaDescribedBy, activeDescribedBy].filter(Boolean).join(' ');

  useEffect(() => {
    setValid(codeValid);
  }, [codeValid]);

  useEffect(() => {
    if (illegalCharacterPressed) {
      const timer = setInterval(() => setIllegalCharacterPressed(false), 250);
      return () => clearInterval(timer);
    }

    return () => {};
  }, [illegalCharacterPressed]);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setCode(e.target.value);
    onChange(e.target.value);
  };

  // Block illegal characters being entered
  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (event) => {
    const { key, metaKey, ctrlKey } = event;

    // If Cmd (Mac) or Ctrl (Windows) is held down, allow the default browser action
    if (metaKey || ctrlKey) return;

    if (key.length === 1) {
      if (!'0123456789'.includes(key)) {
        event.preventDefault();
        setIllegalCharacterPressed(true);
      }
    }
  };

  return (
    <div>
      <Input
        id={id}
        aria-describedby={combinedDescribedBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        tabIndex={0}
        value={code}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        placeholder={t('Verification Code')}
        className="login-input"
        onChange={handleChange}
        onFocus={() => setIsFocussed(true)}
        onBlur={() => setIsFocussed(false)}
        onKeyDown={handleKeyDown}
        autoComplete="one-time-code"
      />
      <VerificationCodeValidator {...{ code, isFocussed, requirementsId, setMissingSummary, setCodeValid }} />
      {/* Hidden area for the voiceover to read out */}
      <VisuallyHidden id={successId}>All verification code requirements met.</VisuallyHidden>
      <VisuallyHidden as="div" aria-live="polite">
        {missingSummary}
      </VisuallyHidden>
    </div>
  );
};

export default VerificationCode;
